import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Spin, Button, Result, Typography, Card, Divider, message } from "antd";
import {
    SafetyCertificateOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    FileImageOutlined,
    CalendarOutlined,
    UserOutlined,
    BookOutlined,
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import API_BASE from "../../config/apiConfig";

const { Title, Text } = Typography;

// 🎨 Couleurs Nomidya
const NOMIDYA_BLUE = "#0054A6";
const NOMIDYA_ORANGE = "#E8860C";

// Fallbacks uniquement si AUCUNE image n'est en base
const DEFAULT_LOGO = "/img/nomidya2.png";
const DEFAULT_BACKGROUND = "/img/2.jpg";

const CertificateVerify = () => {
    const { code } = useParams();
    const certRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [certificateData, setCertificateData] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingImg, setDownloadingImg] = useState(false);

    useEffect(() => {
        const verifyCertificate = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE}/certificats/verify/${code}`);
                setCertificateData(response.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    setError("Ce certificat n'existe pas ou le code de vérification est invalide.");
                } else {
                    setError("Erreur lors de la vérification du certificat.");
                }
            } finally {
                setLoading(false);
            }
        };

        if (code) {
            verifyCertificate();
        }
    }, [code]);

    // Parse settings (base64 du fond/logo y sont stockés)
    const getSettings = () => {
        try {
            return certificateData?.templateSettings
                ? JSON.parse(certificateData.templateSettings)
                : {};
        } catch {
            return {};
        }
    };

    // 🔧 Helper : transforme les signatures (blanc → transparent) avant html2canvas,
    //    puis restaure les originales après. Évite le rectangle blanc dans le PDF/image.
    const prepareSignaturesForCapture = async () => {
        const signatureImgs = certRef.current.querySelectorAll('img[alt="Signature"]');
        const originalSrcs = [];

        for (const img of signatureImgs) {
            originalSrcs.push({
                img,
                src: img.src,
                mixBlendMode: img.style.mixBlendMode,
                background: img.style.background,
            });

            try {
                const canvas = document.createElement("canvas");
                const tempImg = new Image();
                tempImg.crossOrigin = "anonymous";

                await new Promise((resolve, reject) => {
                    tempImg.onload = resolve;
                    tempImg.onerror = reject;
                    tempImg.src = img.src;
                });

                canvas.width = tempImg.naturalWidth;
                canvas.height = tempImg.naturalHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(tempImg, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    // Pixel proche du blanc → transparent
                    if (r >= 240 && g >= 240 && b >= 240) {
                        data[i + 3] = 0;
                    }
                }
                ctx.putImageData(imageData, 0, 0);

                img.src = canvas.toDataURL("image/png");
                img.style.mixBlendMode = "normal";
                img.style.background = "transparent";
            } catch (err) {
                console.warn("Impossible de traiter la signature:", err);
            }
        }

        // Petit délai pour que le DOM applique les nouveaux src
        await new Promise((resolve) => setTimeout(resolve, 100));
        return originalSrcs;
    };

    // 🔧 Restaure les signatures originales après la capture
    const restoreSignatures = (originalSrcs) => {
        originalSrcs.forEach(({ img, src, mixBlendMode, background }) => {
            img.src = src;
            img.style.mixBlendMode = mixBlendMode || "multiply";
            img.style.background = background || "transparent";
        });
    };

    const handleDownloadPDF = async () => {
        if (!certRef.current) return;
        setDownloading(true);
        let originalSrcs = [];
        try {
            // 🔧 Transformer les signatures avant la capture
            originalSrcs = await prepareSignaturesForCapture();

            const canvas = await html2canvas(certRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
            });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("landscape", "pt", "a4");
            pdf.addImage(imgData, "PNG", 0, 0, 842, 595);
            const safeName = (certificateData.userName || "certificat").replace(/\s+/g, "_");
            pdf.save(`${safeName}_certificat.pdf`);
            message.success("Certificat téléchargé en PDF !");
        } catch (err) {
            console.error("Erreur PDF:", err);
            message.error("Erreur lors du téléchargement.");
        } finally {
            // 🔧 Toujours restaurer les signatures, même en cas d'erreur
            restoreSignatures(originalSrcs);
            setDownloading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (!certRef.current) return;
        setDownloadingImg(true);
        let originalSrcs = [];
        try {
            // 🔧 Transformer les signatures avant la capture
            originalSrcs = await prepareSignaturesForCapture();

            const canvas = await html2canvas(certRef.current, {
                scale: 3,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
            });
            const link = document.createElement("a");
            const safeName = (certificateData.userName || "certificat").replace(/\s+/g, "_");
            link.download = `${safeName}_certificat.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            message.success("Certificat téléchargé en image !");
        } catch (err) {
            console.error("Erreur image:", err);
            message.error("Erreur lors du téléchargement.");
        } finally {
            // 🔧 Toujours restaurer les signatures, même en cas d'erreur
            restoreSignatures(originalSrcs);
            setDownloadingImg(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Spin size="large" />
                <Title level={4} style={{ marginTop: 24, color: NOMIDYA_BLUE }}>
                    Vérification du certificat...
                </Title>
            </div>
        );
    }

    if (error || !certificateData?.valid) {
        return (
            <div style={styles.pageContainer}>
                <Card style={styles.errorCard}>
                    <Result
                        icon={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
                        title="Certificat non trouvé"
                        subTitle={error || "Le code de vérification est invalide."}
                        extra={
                            <Button type="primary" href="/" style={styles.backBtn}>
                                Retour à l'accueil
                            </Button>
                        }
                    />
                </Card>
            </div>
        );
    }

    const settings = getSettings();
    const verifyUrl = `${window.location.origin}/certificate/verify/${code}`;

    // ✅ Restauration des VRAIES images depuis les settings (base64)
    //    Fallback sur les images par défaut UNIQUEMENT si absentes
    const bgPreview = settings.backgroundPreview || DEFAULT_BACKGROUND;
    const logoPreview = settings.logoPreview || DEFAULT_LOGO;
    const useImageBg =
        settings.useImageBackground !== undefined ? settings.useImageBackground : true;

    // 🔧 Une image est "sûre" pour html2canvas / prod si c'est du base64 (data:).
    //    Les URLs externes (Google Drive, etc.) sont bloquées en prod par CORS,
    //    ce qui faisait que le template ne s'affichait pas (fond générique).
    const isBase64 = (src) => typeof src === "string" && src.startsWith("data:");

    const bgWidth = settings.backgroundWidth || 650;
    const bgHeight = settings.backgroundHeight || 610;
    const paddingTop = settings.paddingTop || 40;
    const paddingBottom = settings.paddingBottom || 40;
    const logoTopSpacing = settings.logoTopSpacing || 30;
    const logoMarginBottom = settings.logoMarginBottom || 20;
    const logoWidth = settings.logoWidth || 100;
    const logoHeight = settings.logoHeight || 100;
    const textColor = settings.textColor || "#000000";
    const backgroundColor = settings.backgroundColor || "#ffffff";

    return (
        <div style={styles.pageContainer}>
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .cert-container { box-shadow: none !important; }
                }
            `}</style>

            {/* ✅ Carte de vérification */}
            <Card style={styles.verifyCard} className="no-print" bodyStyle={{ padding: 0 }}>
                {/* En-tête bleu Nomidya */}
                <div style={styles.verifyHeader}>
                    <div style={styles.headerIcon}>
                        <SafetyCertificateOutlined style={{ fontSize: 24, color: "#fff" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={4} style={{ margin: 0, color: "#fff" }}>
                            Certificat vérifié
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
                            Ce certificat est authentique et a été délivré par Nomidya.
                        </Text>
                    </div>
                    <span style={styles.validBadge}>
                        <CheckCircleOutlined style={{ marginRight: 5 }} />
                        Valide
                    </span>
                </div>

                <div style={{ padding: "20px 24px" }}>
                    <div style={styles.infoGrid}>
                        <div style={styles.infoItem}>
                            <UserOutlined style={styles.infoIcon} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Titulaire
                                </Text>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>
                                    {certificateData.userName}
                                </div>
                            </div>
                        </div>
                        <div style={styles.infoItem}>
                            <BookOutlined style={styles.infoIcon} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Formation
                                </Text>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>
                                    {certificateData.courseName}
                                </div>
                            </div>
                        </div>
                        <div style={styles.infoItem}>
                            <CalendarOutlined style={styles.infoIcon} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Date de délivrance
                                </Text>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>
                                    {certificateData.dateEnvoi
                                        ? new Date(
                                              certificateData.dateEnvoi
                                          ).toLocaleDateString("fr-FR", {
                                              day: "numeric",
                                              month: "long",
                                              year: "numeric",
                                          })
                                        : "—"}
                                </div>
                            </div>
                        </div>
                        <div style={styles.infoItem}>
                            <SafetyCertificateOutlined style={styles.infoIcon} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Code de vérification
                                </Text>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        fontFamily: "monospace",
                                        color: NOMIDYA_BLUE,
                                    }}
                                >
                                    {certificateData.verificationCode}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Divider style={{ margin: "18px 0" }} />

                    {/* Boutons téléchargement */}
                    <div style={styles.btnRow}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<DownloadOutlined />}
                            loading={downloading}
                            onClick={handleDownloadPDF}
                            style={styles.pdfBtn}
                        >
                            Télécharger en PDF
                        </Button>
                        <Button
                            size="large"
                            icon={<FileImageOutlined />}
                            loading={downloadingImg}
                            onClick={handleDownloadImage}
                            style={styles.imgBtn}
                        >
                            Télécharger en image
                        </Button>
                    </div>
                </div>
            </Card>

            {/* ✅ Certificat visuel — restauré FIDÈLEMENT depuis les settings */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
                <div
                    ref={certRef}
                    className="cert-container"
                    style={{
                        width: `${bgWidth}px`,
                        height: `${bgHeight}px`,
                        paddingTop: `${paddingTop}px`,
                        paddingBottom: `${paddingBottom}px`,
                        paddingLeft: "40px",
                        paddingRight: "40px",
                        // 🔧 Le fond n'est appliqué que si c'est du base64 (sûr en prod).
                        //    Sinon couleur unie (évite fond cassé / blocage CORS en prod).
                        background:
                            useImageBg && isBase64(bgPreview)
                                ? `url(${bgPreview}) center/cover no-repeat`
                                : backgroundColor,
                        color: textColor,
                        borderRadius: "16px",
                        position: "relative",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                        flexShrink: 0,
                    }}
                >
                    {/* 🔧 Logo affiché seulement si base64 (sûr en prod).
                        crossOrigin="anonymous" RETIRÉ : il bloquait l'image en prod. */}
                    {logoPreview && isBase64(logoPreview) && (
                        <img
                            src={logoPreview}
                            alt="Logo"
                            style={{
                                position: "absolute",
                                top: `${logoTopSpacing}px`,
                                left: "30px",
                                width: `${logoWidth}px`,
                                height: `${logoHeight}px`,
                            }}
                        />
                    )}

                    <div
                        style={{
                            marginTop: `${
                                logoTopSpacing + logoHeight + logoMarginBottom
                            }px`,
                        }}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: certificateData.generatedHtml,
                            }}
                        />
                    </div>

                    <div
                        style={{
                            position: "absolute",
                            bottom: "30px",
                            right: "30px",
                        }}
                    >
                        <QRCodeCanvas
                            value={verifyUrl}
                            size={80}
                            bgColor="white"
                            fgColor="#000"
                        />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="no-print" style={styles.footer}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Certificat délivré par <strong>Nomidya E-Learning</strong> •
                    Vérifiable à tout moment via le QR code ou le lien de vérification
                </Text>
            </div>
        </div>
    );
};

const styles = {
    pageContainer: {
        minHeight: "100vh",
        background: "#f0f2f5",
        padding: "40px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    loadingContainer: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f0f2f5",
    },
    verifyCard: {
        maxWidth: 700,
        width: "100%",
        borderRadius: 16,
        boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
        overflow: "hidden",
        border: "none",
    },
    verifyHeader: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: `linear-gradient(135deg, ${NOMIDYA_BLUE} 0%, #0067cc 100%)`,
        padding: "18px 22px",
    },
    headerIcon: {
        width: 44,
        height: 44,
        borderRadius: 12,
        background: NOMIDYA_ORANGE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    validBadge: {
        background: "#f6ffed",
        color: "#389e0d",
        fontSize: 12,
        fontWeight: 600,
        padding: "5px 12px",
        borderRadius: 8,
        whiteSpace: "nowrap",
    },
    errorCard: {
        maxWidth: 600,
        width: "100%",
        borderRadius: 16,
        boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
    },
    infoGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 12,
    },
    infoItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        background: "#fafafa",
        borderRadius: 10,
    },
    infoIcon: {
        fontSize: 20,
        color: NOMIDYA_BLUE,
        marginTop: 2,
    },
    btnRow: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
    },
    pdfBtn: {
        flex: 1,
        minWidth: 180,
        height: 46,
        borderRadius: 10,
        background: NOMIDYA_BLUE,
        borderColor: NOMIDYA_BLUE,
        fontSize: 15,
        fontWeight: 600,
    },
    imgBtn: {
        flex: 1,
        minWidth: 180,
        height: 46,
        borderRadius: 10,
        borderColor: NOMIDYA_ORANGE,
        color: NOMIDYA_ORANGE,
        fontSize: 15,
        fontWeight: 600,
    },
    backBtn: {
        borderRadius: 10,
        background: NOMIDYA_BLUE,
        border: "none",
    },
    footer: {
        marginTop: 30,
        textAlign: "center",
        padding: "16px 20px",
        background: "rgba(255,255,255,0.7)",
        borderRadius: 12,
        maxWidth: 700,
        width: "100%",
    },
};

export default CertificateVerify;